import { Component, Input, Inject, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { debounceTime, startWith } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { cloneDeep } from 'lodash';

@Component({
    selector: 'sfx-filters-rule',
    templateUrl: './filters-rule.component.html',
    styleUrls: ['./filters-rule.component.scss']
})
export class FiltersRuleComponent implements OnInit, OnChanges {

    @Input() index!: number;
    @Input() appliedRule: any;
    @Input() fixedRule?: boolean;
    @Input() first!: boolean;
    @Input() last!: boolean;
    @Input() pipeline: any;
    @Input() entity!: string;
    @Input() group!: string;
    @Input() filterFields: any[] = [];

    @Input() deleteRule!: any;
    @Input() onRuleUpdate!: (args: { newRule: any }) => void;
    @Input() onApply!: () => void;

    searchControl = new FormControl('', Validators.required);
    selectedProp: any = null;
    property: any;
    chosen = false;
    entities: any;
    entityPlural = '';
    filterProperties: any[] = [];
    standardPropertyList: any[] = [];
    filteredProperties: any[] = [];
    isDisabled = false;
    noEditAllowed = false;
    currentUser: any;
    searchText = '';
    propertyFilterForm: FormGroup;

    private firstSearch = true;

    constructor(
        private fb: FormBuilder,
        @Inject('sfWalkthrough') private walkthroughService: any,
        @Inject('stateService') private stateService: any,
        @Inject('modelService') private modelService: any,
        private translate: TranslateService
    ) {
        this.propertyFilterForm = this.fb.group({
            autocompleteField: new FormControl('')
        });
    }

    ngOnInit(): void {
        this.currentUser = this.modelService.me;

        this.entities = {
            accounts: 'account',
            contacts: 'contact',
            leads: 'lead',
            opportunities: 'opportunity',
            tags: 'tag',
            tasks: 'task',
            campaigns: 'campaign',
            workflows: 'workflow'
        };

        this.entityPlural = (this.entity === 'opportunity') ? 'opportunities' : (this.entity + 's');

        if (this.appliedRule && this.appliedRule.id) {
            this.setupPropertyFromAppliedRule();
            this.checkIfDisabled();
        }
        else {
            this.orderFilterFields();
        }

        this.searchControl.valueChanges
            .pipe(
                startWith(''),
                debounceTime(150) // Matches md-delay="150"
            )
            .subscribe((searchText) => {
                this.filteredProperties = this.getMatches(searchText as string);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.filterFields) {
            if (this.appliedRule?.id && !this.property) {
                this.setupPropertyFromAppliedRule();
            }
            else if (changes.filterFields.previousValue && changes?.filterFields?.previousValue?.length) {
                this.orderFilterFields();
            }

            this.checkIfDisabled();
        }
    }

    setupPropertyFromAppliedRule(): void {
        this.property = this.appliedRule;
        const disabledText = this.property.enabled === false ? ` (${this.translate.instant('disabled')})` : '';
        if (this.property.group === 'Lead') {
            this.property.display_name = `${this.property.label}${disabledText}`;
        }
        else {
            this.property.display_name = `${this.property.group} ${this.property.label}${disabledText}`;
        }

        this.property.index = this.index;

        if (this.property.display_entity) {
            this.property.entity_ui = this.property.display_entity;
        }
        else if (this.property.entity.toLowerCase() === 'person') {
            this.property.entity_ui = 'Contact';
        }
        else {
            this.property.entity_ui = this.property.entity.charAt(0).toUpperCase() + this.property.entity.slice(1);
        }

        if (this.property.fixed_operator) {
            this.property.operator = this.property.fixed_operator;
        }

        this.chosen = true;
    }

    getMatches(searchText: string | null): any[] {
        let matches: any[] = [];

        this.filterProperties.forEach((filterProperty) => {
            let title = filterProperty.title;
            if (title === 'Person') {
                title = 'Contact';
            }

            let entityMatches;

            if (!searchText) {
                entityMatches = filterProperty.properties;
            }

            if (title && searchText && title.toLowerCase().includes(searchText.toLowerCase())) {
                entityMatches = filterProperty.properties;
            }

            if (!entityMatches && searchText) {
                entityMatches = filterProperty.properties.filter((property: any) =>
                    property.label.toLowerCase().includes(searchText.toLowerCase())
                );
            }

            const subHeader = {
                title,
                entity: filterProperty.entity,
                label: title,
                subheader: true
            };

            entityMatches = entityMatches.map((property: any) => {
                property.entity_ui = title;
                return property;
            });

            if (entityMatches.length > 0) {
                entityMatches.unshift(subHeader);
            }

            matches = [...matches, ...entityMatches];
        });

        this.standardPropertyList = matches;

        if (this.firstSearch) {
            this.firstSearch = false;

            // Only return first 10 items (md-dropdown-items = 8) immediately so only a couple of watchers get created, see comment on $timeout above
            return cloneDeep(matches.slice(0, 10));
        }

        return cloneDeep(matches);
    }

    delete(): void {
        this.deleteRule();
    }

    boundOnPropertyChange = (event: any) => this.onPropertyChange(event);

    private onPropertyChange(event: any): void {
        this.property.operator = event.operator;
        this.property.value = event.value;
        this.property.raw_value = event.raw_value;

        this.checkIfDisabled();
        this.onRuleUpdate(this.property);
    }

    selectedItemChange(property: any): void {
        if (!property || property.title) {
            this.searchText = '';
            return;
        }

        if (property && !property.title) {
            if (this.property.fixed_operator) {
                this.property.operator = this.property.fixed_operator;
            }

            this.chosen = true;
        }
    }

    onOptionSelected(selected: any) {
        this.property = cloneDeep(selected);
        this.selectedProp = selected;
        this.searchControl.setValue(selected.label);
        this.selectedItemChange(selected);
    }

    getLabelExtension(property: any): string {
        if (property && property.enabled === false) {
            return ' (' + this.translate.instant('disabled field') + ')';
        }

        return '';
    }

    displayFn(item: any): string {
        return item ? item.label : '';
    }

    highlight(text: string, search: string | null): string {
        if (!search) {
            return text;
        }

        const escapedSearch = search.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); // Escape special regex chars
        const re = new RegExp(escapedSearch, 'gi');

        return text.replace(re, (match) => `<b>${match}</b>`);
    }

    showPipelineNotice(): boolean {
        return this.entityPlural === 'opportunities' &&
            this.property &&
            (this.property.id === 'pipeline.id' || this.property.id === 'opportunity.pipeline.id');
    }

    getPipelineNoticeText(): string | undefined {
        const pipelineNoticeMap: { [key: string]: string } = {
            opportunities: this.translate.instant('Please select the pipeline at the top of the page.'),
            report: this.translate.instant('Please use the dashboard filters for this.')
        };

        return Object.entries(pipelineNoticeMap).find((keyValue) => {

            return this.stateService.current.name.toLowerCase().includes(keyValue[0]);
        })?.[1];
    }

    private orderFilterFields(): void {
        const groupOrder = ['Account', 'Contact', 'Lead', 'Opportunity', 'Task', 'Tag', 'Campaign', 'Workflow'];

        if (!this.walkthroughService.isShowing()) {
            this.entities.contacts = 'person';

            const entityProperties = {
                title: this.group,
                entity: this.entity,
                properties: this.filterFields.filter((property) =>
                    !property.display_entity && property.group === this.group
                )
            };

            const indexToDelete = groupOrder.indexOf(this.group);
            this.filterProperties = [entityProperties];

            if (indexToDelete > -1) {
                groupOrder.splice(indexToDelete, 1);
            }

            groupOrder.forEach((group) => {
                this.filterProperties.push({
                    title: group,
                    entity: group.toLowerCase(),
                    properties: this.filterFields.filter((property) => {
                        if (property.display_entity) {
                            return property.display_entity.toLowerCase() === group.toLowerCase();
                        }

                        return property.group.toLowerCase() === group.toLowerCase();
                    })
                });
            });
        }
    }

    checkIfDisabled(): void {
        const enabledRule = this.filterFields.find((field) => {
            // eslint-disable-next-line angular/definedundefined
            if (this.appliedRule.pipeline !== undefined && field.pipeline !== undefined && this.appliedRule.pipeline !== field.pipeline) {
                return false;
            }

            return field.id === this.appliedRule.id || field.query_builder_id === this.appliedRule.id;
        });

        if (!enabledRule && this.appliedRule.id && !this.appliedRule.id.includes('custom')) {
            this.noEditAllowed = true;
        }

        if (!this.appliedRule.id) {
            this.isDisabled = false;
        }
        else if (!enabledRule && !this.noEditAllowed) {
            this.isDisabled = true;
        }
        else {
            this.isDisabled = false;
        }

        if (this.appliedRule.archived) {
            this.isDisabled = true;
        }

        if (!this.appliedRule.search_entity &&
            (this.appliedRule.raw_value || []).some((value: any) => value.archived)) {
            this.isDisabled = true;
        }

        if (this.property && this.property.disabled !== this.isDisabled) {
            this.property.disabled = this.isDisabled;
            this.onRuleUpdate(this.property);
        }

        // eslint-disable-next-line angular/definedundefined
        if (this.property && enabledRule && this.property.enabled !== undefined && enabledRule.enabled !== undefined &&
            this.property.enabled !== enabledRule.enabled) {
            this.property.enabled = enabledRule.enabled;
            this.appliedRule.enabled = enabledRule.enabled;
            this.onRuleUpdate(this.property);
        }
    }
}
